// src/features/kommunikation/services/CommunicationDocumentService.ts
// VOLLSTÄNDIGE VERSION mit sendTestDocumentNow für Testversand und replaceDocument fuer PDF-Austausch

import bcrypt from 'bcryptjs';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../../../shared/services/supabase';
import { netlifyFetch } from '../../../shared/services/netlifyFetch';
import { credentialsService } from './CredentialsService';
import {
  CommunicationDocument,
  DocumentCreateInput,
  DocumentWithCredentials,
  DocumentFilter,
  DocumentStats
} from '../types';

/**
 * Haupt-Service für Kommunikations-Dokumente
 */
export class CommunicationDocumentService {
  
  // ============================================
  // PRIVATE HELPER METHODEN
  // ============================================
  
  private generateToken(): string {
    return uuidv4().replace(/-/g, '');
  }
  
  private async hashPassword(passwort: string): Promise<string> {
    const salt = await bcrypt.genSalt(10);
    return bcrypt.hash(passwort, salt);
  }
  
  // ============================================
  // PUBLIC METHODEN
  // ============================================
  
  /**
   * 1. Lookup Beraternummer aus customers Tabelle via Mandantennummer
   */
  async lookupBeraternummer(mandantennummer: string): Promise<string | null> {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('beraternummer')
        .eq('mandantennummer', mandantennummer)
        .eq('deleted', false)
        .single();
      
      if (error) {
        if (error.code === 'PGRST116') {
          console.log(`Keine Beraternummer für Mandant ${mandantennummer} gefunden`);
          return null;
        }
        console.error('Error looking up Beraternummer:', error);
        return null;
      }
      
      return data?.beraternummer || null;
    } catch (error) {
      console.error('Error in lookupBeraternummer:', error);
      return null;
    }
  }
  
  /**
   * 2. Erstellt ein neues Dokument in der Datenbank
   */
  async createDocument(input: DocumentCreateInput): Promise<{ success: boolean; documentId?: string; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('communication_documents')
        .insert({
          mandantennummer: input.mandantennummer,
          personalnummer: input.personalnummer,
          monat: input.monat,
          jahr: input.jahr,
          dokumententyp: input.dokumententyp,
          beraternummer: input.beraternummer,
          original_filename: input.original_filename,
          storage_path: input.storage_path,
          file_size: input.file_size,
          versand_datum: input.versand_datum.toISOString(),
          zugriffs_token: input.zugriffs_token,
          zugriffs_passwort_hash: input.zugriffs_passwort_hash,
          token_ablaufdatum: input.token_ablaufdatum.toISOString(),
          uploaded_by_email: input.uploaded_by_email,
          ist_sonderversand: input.ist_sonderversand
        })
        .select('id')
        .single();
      
      if (error) {
        console.error('Error creating document:', error);
        return {
          success: false,
          error: `Dokument speichern fehlgeschlagen: ${error.message}`
        };
      }
      
      return {
        success: true,
        documentId: data.id
      };
      
    } catch (error) {
      console.error('Error in createDocument:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  }
  
  /**
   * 3. Vollständiger Upload-Flow: Storage + DB + Token-Generierung
   */
  async uploadDocumentComplete(
    mandantennummer: string,
    personalnummer: string,
    monat: number,
    jahr: number,
    dokumententyp: string,
    originalFilename: string,
    storagePath: string,
    fileSize: number,
    versandDatum: Date,
    passwort: string,
    uploadedByEmail: string
  ): Promise<{ success: boolean; documentId?: string; token?: string; error?: string }> {
    try {
      const beraternummer = await this.lookupBeraternummer(mandantennummer);
      const token = this.generateToken();
      const passwortHash = await this.hashPassword(passwort);
      
      // 18 Monate Ablaufdatum
      const ablaufdatum = new Date(versandDatum.getTime());
      ablaufdatum.setMonth(ablaufdatum.getMonth() + 18);
      
      const result = await this.createDocument({
        mandantennummer,
        personalnummer,
        monat,
        jahr,
        dokumententyp,
        beraternummer,
        original_filename: originalFilename,
        storage_path: storagePath,
        file_size: fileSize,
        versand_datum: versandDatum,
        zugriffs_token: token,
        zugriffs_passwort_hash: passwortHash,
        token_ablaufdatum: ablaufdatum,
        uploaded_by_email: uploadedByEmail,
        ist_sonderversand: false
      });
      
      if (!result.success) {
        return {
          success: false,
          error: result.error
        };
      }
      
      return {
        success: true,
        documentId: result.documentId,
        token
      };
      
    } catch (error) {
      console.error('Error in uploadDocumentComplete:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Upload fehlgeschlagen'
      };
    }
  }
  
  /**
   * 3b. NEU (Sonderversand): Vollständiger Upload-Flow ohne Monat/Jahr.
   *
   * Legt ein Sonderversand-Dokument an (monat/jahr null, ist_sonderversand true,
   * dokumententyp = 'Sonderversand'). Das Versanddatum ist "jetzt", da der
   * Sonderversand sofort erfolgt. Token, Passwort-Hash und 18-Monats-Gültigkeit
   * funktionieren identisch zum regulären Versand.
   */
  async uploadSonderversandComplete(
    mandantennummer: string,
    personalnummer: string,
    originalFilename: string,
    storagePath: string,
    fileSize: number,
    passwort: string,
    uploadedByEmail: string
  ): Promise<{ success: boolean; documentId?: string; token?: string; error?: string }> {
    try {
      const beraternummer = await this.lookupBeraternummer(mandantennummer);
      const token = this.generateToken();
      const passwortHash = await this.hashPassword(passwort);
      
      // Sofortversand: Versanddatum ist jetzt
      const versandDatum = new Date();
      
      // 18 Monate Ablaufdatum
      const ablaufdatum = new Date(versandDatum.getTime());
      ablaufdatum.setMonth(ablaufdatum.getMonth() + 18);
      
      const result = await this.createDocument({
        mandantennummer,
        personalnummer,
        monat: null,
        jahr: null,
        dokumententyp: 'Sonderversand',
        beraternummer,
        original_filename: originalFilename,
        storage_path: storagePath,
        file_size: fileSize,
        versand_datum: versandDatum,
        zugriffs_token: token,
        zugriffs_passwort_hash: passwortHash,
        token_ablaufdatum: ablaufdatum,
        uploaded_by_email: uploadedByEmail,
        ist_sonderversand: true
      });
      
      if (!result.success) {
        return {
          success: false,
          error: result.error
        };
      }
      
      return {
        success: true,
        documentId: result.documentId,
        token
      };
      
    } catch (error) {
      console.error('Error in uploadSonderversandComplete:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Upload fehlgeschlagen'
      };
    }
  }
  
  /**
   * 4. Lädt alle Dokumente mit optionalem Filter
   */
  async getDocuments(filter?: DocumentFilter): Promise<CommunicationDocument[]> {
    try {
      let query = supabase
        .from('communication_documents')
        .select('*')
        .order('versand_datum', { ascending: true })
        .limit(5000);
      
      if (filter?.mandantennummer) {
        query = query.eq('mandantennummer', filter.mandantennummer);
      }
      
      if (filter?.beraternummer) {
        query = query.eq('beraternummer', filter.beraternummer);
      }
      
      if (filter?.versand_status) {
        query = query.eq('versand_status', filter.versand_status);
      }
      
      if (filter?.ist_sonderversand) {
        // Sonderversand-Ansicht: ueber alle Zeitraeume hinweg, Monat/Jahr werden ignoriert
        query = query.eq('ist_sonderversand', true);
      } else {
        if (filter?.monat) {
          query = query.eq('monat', filter.monat);
        }
        
        if (filter?.jahr) {
          query = query.eq('jahr', filter.jahr);
        }
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error('Error loading documents:', error);
        throw new Error(`Dokumente laden fehlgeschlagen: ${error.message}`);
      }
      
      return data || [];
    } catch (error) {
      console.error('Error in getDocuments:', error);
      throw error;
    }
  }
  
  /**
   * 5. Lädt Dokumente mit zugehörigen Credentials
   */
  async getDocumentsWithCredentials(filter?: DocumentFilter): Promise<DocumentWithCredentials[]> {
    try {
      const documents = await this.getDocuments(filter);
      
      const documentsWithCredentials: DocumentWithCredentials[] = await Promise.all(
        documents.map(async (doc) => {
          const credential = await credentialsService.findCredential({
            beraternummer: doc.beraternummer || '',
            mandantennummer: doc.mandantennummer,
            personalnummer: doc.personalnummer
          });
          
          return {
            ...doc,
            credential: credential || null,
            hasCredential: !!credential,
            canSend: !!credential
          };
        })
      );
      
      return documentsWithCredentials;
    } catch (error) {
      console.error('Error in getDocumentsWithCredentials:', error);
      throw error;
    }
  }
  
  /**
   * 6. Löscht ein Dokument (soft delete via versand_status)
   */
  async deleteDocument(documentId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('communication_documents')
        .update({ versand_status: 'abgebrochen' })
        .eq('id', documentId);
      
      if (error) {
        console.error('Error deleting document:', error);
        return {
          success: false,
          error: `Löschen fehlgeschlagen: ${error.message}`
        };
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error in deleteDocument:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  }
  
  /**
   * 7. Lädt Statistiken über Dokumente
   * Verwendet serverseitige Count-Queries (kein Row-Limit von 1000)
   */
  async getStats(): Promise<DocumentStats> {
    try {
      // Parallele serverseitige Count-Queries
      const [totalResult, geplantResult, versendetResult, fehlerResult] = await Promise.all([
        supabase
          .from('communication_documents')
          .select('*', { count: 'exact', head: true }),
        supabase
          .from('communication_documents')
          .select('*', { count: 'exact', head: true })
          .eq('versand_status', 'geplant'),
        supabase
          .from('communication_documents')
          .select('*', { count: 'exact', head: true })
          .eq('versand_status', 'versendet'),
        supabase
          .from('communication_documents')
          .select('*', { count: 'exact', head: true })
          .eq('versand_status', 'fehler')
      ]);

      const total = totalResult.count ?? 0;
      const geplant = geplantResult.count ?? 0;
      const versendet = versendetResult.count ?? 0;
      const fehler = fehlerResult.count ?? 0;

      // Ohne Credentials: Nur geplante Dokumente prüfen (kleinere Menge, performanter)
      let ohne_credentials = 0;
      if (geplant > 0) {
        const { data: geplanteDokumente } = await supabase
          .from('communication_documents')
          .select('beraternummer, mandantennummer, personalnummer')
          .eq('versand_status', 'geplant');

        if (geplanteDokumente) {
          const credentialChecks = await Promise.all(
            geplanteDokumente.map(async (doc) => {
              const credential = await credentialsService.findCredential({
                beraternummer: doc.beraternummer || '',
                mandantennummer: doc.mandantennummer,
                personalnummer: doc.personalnummer
              });
              return !credential;
            })
          );
          ohne_credentials = credentialChecks.filter(Boolean).length;
        }
      }

      return {
        total,
        geplant,
        versendet,
        fehler,
        ohne_credentials
      };
    } catch (error) {
      console.error('Error in getStats:', error);
      return {
        total: 0,
        geplant: 0,
        versendet: 0,
        fehler: 0,
        ohne_credentials: 0
      };
    }
  }
  
  /**
   * 8. Aktualisiert den Versandstatus eines Dokuments
   */
  async updateVersandStatus(
    documentId: string,
    status: 'geplant' | 'versendet' | 'fehler' | 'abgebrochen',
    fehlerNachricht?: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const updateData: Record<string, unknown> = {
        versand_status: status,
        updated_at: new Date().toISOString()
      };
      
      if (status === 'versendet') {
        updateData.versendet_am = new Date().toISOString();
      }
      
      if (fehlerNachricht) {
        updateData.fehler_nachricht = fehlerNachricht;
      }
      
      const { error } = await supabase
        .from('communication_documents')
        .update(updateData)
        .eq('id', documentId);
      
      if (error) {
        console.error('Error updating status:', error);
        return {
          success: false,
          error: `Status-Update fehlgeschlagen: ${error.message}`
        };
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error in updateVersandStatus:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  }
  
  /**
   * 9. Markiert Dokument als versendet
   */
  async markDocumentAsSent(documentId: string): Promise<{ success: boolean; error?: string }> {
    return this.updateVersandStatus(documentId, 'versendet');
  }
  
  /**
   * 10. Findet Dokumente für geplanten Versand
   */
  async getDocumentsForScheduledSending(currentTime: Date): Promise<DocumentWithCredentials[]> {
    try {
      const { data, error } = await supabase
        .from('communication_documents')
        .select('*')
        .eq('versand_status', 'geplant')
        .lte('versand_datum', currentTime.toISOString());
      
      if (error) {
        console.error('Error loading scheduled documents:', error);
        return [];
      }
      
      if (!data || data.length === 0) {
        return [];
      }
      
      const documentsWithCredentials: DocumentWithCredentials[] = await Promise.all(
        data.map(async (doc) => {
          const credential = await credentialsService.findCredential({
            beraternummer: doc.beraternummer || '',
            mandantennummer: doc.mandantennummer,
            personalnummer: doc.personalnummer
          });
          
          return {
            ...doc,
            credential: credential || null,
            hasCredential: !!credential,
            canSend: !!credential
          };
        })
      );
      
      return documentsWithCredentials.filter(doc => doc.canSend);
      
    } catch (error) {
      console.error('Error in getDocumentsForScheduledSending:', error);
      return [];
    }
  }
  
  /**
   * 11. Erstellt einen Versand-Log-Eintrag
   */
  async createSendLog(
    documentId: string,
    empfaenger: string,
    betreff: string,
    erfolg: boolean,
    fehler?: string
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('communication_send_log')
        .insert({
          document_id: documentId,
          email_empfaenger: empfaenger,
          email_betreff: betreff,
          email_body: null,
          versand_erfolg: erfolg,
          fehler_details: fehler
        });
      
      if (error) {
        console.error('Error creating send log:', error);
      }
    } catch (error) {
      console.error('Error in createSendLog:', error);
    }
  }
  
  /**
   * 12. Validiert Token und Passwort für Public Access
   */
  async validateTokenAccess(
    token: string,
    passwort: string
  ): Promise<{ valid: boolean; document?: CommunicationDocument; error?: string }> {
    try {
      const { data: documents, error: findError } = await supabase
        .from('communication_documents')
        .select('*')
        .eq('zugriffs_token', token)
        .limit(1);
      
      if (findError || !documents || documents.length === 0) {
        return {
          valid: false,
          error: 'Ungültiger Zugriffscode'
        };
      }
      
      const document = documents[0];
      
      const ablaufdatum = new Date(document.token_ablaufdatum);
      if (ablaufdatum < new Date()) {
        return {
          valid: false,
          error: 'Zugriffscode abgelaufen'
        };
      }
      
      const isValidPassword = await bcrypt.compare(passwort, document.zugriffs_passwort_hash);
      if (!isValidPassword) {
        return {
          valid: false,
          error: 'Falsches Passwort'
        };
      }
      
      await supabase
        .from('communication_documents')
        .update({
          zugegriffen_am: new Date().toISOString(),
          zugriffs_count: (document.zugriffs_count || 0) + 1
        })
        .eq('id', document.id);
      
      return {
        valid: true,
        document
      };
      
    } catch (error) {
      console.error('Error in validateTokenAccess:', error);
      return {
        valid: false,
        error: 'Validierung fehlgeschlagen'
      };
    }
  }
  
  /**
   * 13. Sendet ein Dokument sofort (erstmalig oder erneut)
   */
  async sendDocumentNow(documentId: string): Promise<{ success: boolean; error?: string }> {
    try {
      console.log(`[SendDocumentNow] Starting immediate send for document ${documentId}`);
      
      const { data: document, error: docError } = await supabase
        .from('communication_documents')
        .select('*')
        .eq('id', documentId)
        .single();
      
      if (docError || !document) {
        return {
          success: false,
          error: `Dokument nicht gefunden: ${docError?.message || 'Unbekannter Fehler'}`
        };
      }
      
      const credential = await credentialsService.findCredential({
        beraternummer: document.beraternummer || '',
        mandantennummer: document.mandantennummer,
        personalnummer: document.personalnummer
      });
      
      if (!credential) {
        return {
          success: false,
          error: 'Keine Credentials für dieses Dokument gefunden. Bitte zuerst Credentials hochladen.'
        };
      }
      
      const response = await netlifyFetch('/.netlify/functions/send-communication-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          document_id: documentId,
          credential_id: credential.id
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'E-Mail-Versand fehlgeschlagen';
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        
        return {
          success: false,
          error: errorMessage
        };
      }
      
      const result = await response.json();
      
      if (result.success) {
        console.log(`[SendDocumentNow] Successfully sent document ${documentId}`);
        return { success: true };
      } else {
        return {
          success: false,
          error: result.error || 'Versand fehlgeschlagen'
        };
      }
      
    } catch (error) {
      console.error('Error in sendDocumentNow:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler beim Versand'
      };
    }
  }
  
  /**
   * 14. Sendet ein Dokument als Test an lohn@xtax.de
   * Status bleibt unverändert, Betreff wird mit [TEST] markiert
   */
  async sendTestDocumentNow(documentId: string): Promise<{ success: boolean; error?: string }> {
    try {
      console.log(`[SendTestDocumentNow] Starting test send for document ${documentId}`);
      
      const { data: document, error: docError } = await supabase
        .from('communication_documents')
        .select('*')
        .eq('id', documentId)
        .single();
      
      if (docError || !document) {
        return {
          success: false,
          error: `Dokument nicht gefunden: ${docError?.message || 'Unbekannter Fehler'}`
        };
      }
      
      const credential = await credentialsService.findCredential({
        beraternummer: document.beraternummer || '',
        mandantennummer: document.mandantennummer,
        personalnummer: document.personalnummer
      });
      
      if (!credential) {
        return {
          success: false,
          error: 'Keine Credentials für dieses Dokument gefunden. Bitte zuerst Credentials hochladen.'
        };
      }
      
      // Rufe Netlify Function mit test_mode: true auf
      const response = await netlifyFetch('/.netlify/functions/send-communication-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          document_id: documentId,
          credential_id: credential.id,
          test_mode: true
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Testversand fehlgeschlagen';
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        
        return {
          success: false,
          error: errorMessage
        };
      }
      
      const result = await response.json();
      
      if (result.success) {
        console.log(`[SendTestDocumentNow] Successfully sent test for document ${documentId}`);
        return { success: true };
      } else {
        return {
          success: false,
          error: result.error || 'Testversand fehlgeschlagen'
        };
      }
      
    } catch (error) {
      console.error('Error in sendTestDocumentNow:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler beim Testversand'
      };
    }
  }
  
  /**
   * 15. NEU: Tauscht das PDF eines Dokuments im Storage aus.
   * Empfaenger-Link und Passwort bleiben unveraendert; Versandstatus bleibt
   * unveraendert; es wird KEINE E-Mail versendet. Der Aufruf delegiert die
   * privileged Storage-Operation an die Netlify Function
   * replace-communication-document.
   */
  async replaceDocument(
    documentId: string,
    fileBase64: string,
    filename: string,
    requestingUserEmail: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      console.log(`[ReplaceDocument] Starting replacement for document ${documentId}`);
      
      const response = await netlifyFetch('/.netlify/functions/replace-communication-document', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          documentId,
          newFileBase64: fileBase64,
          newFilename: filename,
          requestingUserEmail
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Austausch fehlgeschlagen';
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        return { success: false, error: errorMessage };
      }
      
      const result = await response.json();
      
      if (result.success) {
        console.log(`[ReplaceDocument] Successfully replaced document ${documentId}`);
        if (result.auditWarning) {
          console.warn(`[ReplaceDocument] Audit-Warnung: ${result.auditWarning}`);
        }
        return { success: true };
      } else {
        return {
          success: false,
          error: result.error || 'Austausch fehlgeschlagen'
        };
      }
    } catch (error) {
      console.error('Error in replaceDocument:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler beim Austausch'
      };
    }
  }
}

export const communicationDocumentService = new CommunicationDocumentService();
